// eslint-disable-next-line wandb/no-deprecated-imports
import {Checkbox} from '@wandb/weave/components';
// eslint-disable-next-line wandb/no-deprecated-imports
import {TextField} from '@wandb/weave/components/Form/TextField';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React from 'react';
import {create} from 'zustand';
import {useShallow} from 'zustand/react/shallow';

import {EventTriggeringConditionType} from '../TriggerEventTypes';
import {Trigger} from '../TriggersTable/utilTypes';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {ErrorText} from './CreateTriggerDrawerLoaded.styles';
import {
  getRunFilterConfigFromTriggerFilter,
  isValidRunFilterConfig,
  RunFilter,
  RunFilterChunk,
  RunFilterConfig,
} from './RunFilterShared';
import {ChangeDirection} from './RunMetricChangeSection';
import {BoldLabel, SeparatedChunk} from './shared';

// Raw z-score filter from API/JSON (matches Go's ZScoreRunMetricFilter)
type RawZScoreFilter = {
  name: string;
  window_size: number;
  threshold: number;
  change_dir: ChangeDirection;
  panelLink: string | undefined;
};

export type ZscoreMetricFilter = {
  metricName: string;
  threshold: string;
  windowSize: string;
  changeDirection: {
    isAboveChecked: boolean;
    isBelowChecked: boolean;
  };
  panelLink: string | undefined;
};

export type RunMetricZscoreConfig = {
  metricFilter: Omit<ZscoreMetricFilter, 'metricName'> & {
    metricName: string | undefined;
  };
  runFilter: RunFilterConfig;
  allowAutoFocus: boolean;
};

const initialRunMetricZscoreConfig: RunMetricZscoreConfig = {
  metricFilter: {
    metricName: undefined,
    threshold: '3.0',
    windowSize: '30',
    changeDirection: {
      isAboveChecked: true,
      isBelowChecked: true,
    },
    panelLink: undefined as string | undefined,
  },
  runFilter: {
    isUsernameFilterEnabled: false,
    username: undefined,
    isRunNameFilterEnabled: false,
    runName: undefined,
  },
  allowAutoFocus: true,
};

export const useRunMetricZscoreStore = create<RunMetricZscoreConfig>()(() => ({
  ...initialRunMetricZscoreConfig,
}));

export const RunMetricZscoreActions = {
  initAsync: async (suggestedMetricName?: string, panelLink?: string) => {
    useRunMetricZscoreStore.setState({
      ...initialRunMetricZscoreConfig,
      metricFilter: {
        ...initialRunMetricZscoreConfig.metricFilter,
        metricName: suggestedMetricName,
        panelLink: panelLink,
      },
      allowAutoFocus: suggestedMetricName == null,
    });
  },

  initFromTrigger: (trigger: Trigger) => {
    const config = getRunMetricZscoreConfig(trigger);
    if (config) {
      useRunMetricZscoreStore.setState(config);
    }
  },
  setMetricName: (metricName: string) => {
    setMetricFilter({metricName});
  },
  setThreshold: (threshold: string) => {
    setMetricFilter({threshold});
  },
  setWindowSize: (windowSize: string) => {
    setMetricFilter({windowSize});
  },
  setRunName: (runName: string) => {
    setRunFilter({runName});
  },
  setIsRunNameFilterEnabled: (isRunNameFilterEnabled: boolean) => {
    setRunFilter({isRunNameFilterEnabled});
  },
  setUsername: (username: string | undefined) => {
    setRunFilter({username});
  },
  setIsUsernameFilterEnabled: (isUsernameFilterEnabled: boolean) => {
    setRunFilter({isUsernameFilterEnabled});
  },
  setIsAboveChecked: (isAboveChecked: boolean) => {
    setMetricFilter({
      changeDirection: {
        ...useRunMetricZscoreStore.getState().metricFilter.changeDirection,
        isAboveChecked,
      },
    });
  },
  setIsBelowChecked: (isBelowChecked: boolean) => {
    setMetricFilter({
      changeDirection: {
        ...useRunMetricZscoreStore.getState().metricFilter.changeDirection,
        isBelowChecked,
      },
    });
  },
};

export const getRunMetricZscoreConfig = (
  trigger: Trigger
): RunMetricZscoreConfig | undefined => {
  if (
    !trigger.triggeringCondition.filter ||
    trigger.triggeringCondition.eventType !==
      EventTriggeringConditionType.RunMetricZscore
  ) {
    return undefined;
  }

  try {
    const parsedFilter = JSON.parse(trigger.triggeringCondition.filter);

    const runFilter = getRunFilterConfigFromTriggerFilter(parsedFilter);
    if (!runFilter) {
      console.error(
        'Failed to parse run filter config from trigger filter:',
        parsedFilter
      );
      return undefined;
    }

    if (
      !parsedFilter.metric_filter &&
      !parsedFilter.run_metric_filter?.zscore_filter
    ) {
      console.error('Failed to parse run metric config:', parsedFilter);
      return undefined;
    }

    const rawZscoreFilter: RawZScoreFilter =
      parsedFilter.run_metric_filter.zscore_filter;

    const result = {
      metricFilter: {
        metricName: rawZscoreFilter.name,
        threshold: rawZscoreFilter.threshold.toString(),
        windowSize: rawZscoreFilter.window_size.toString(),
        changeDirection: getCheckboxesFromChangeDirection(
          rawZscoreFilter.change_dir
        ),
        panelLink: rawZscoreFilter.panelLink,
      },
      runFilter,
      allowAutoFocus: false,
    };
    return result;
  } catch (e) {
    console.error(
      'Failed to parse run metric z-score config from trigger:',
      e,
      trigger
    );
    return undefined;
  }
};

const setMetricFilter = (metricFilter: Partial<ZscoreMetricFilter>) => {
  useRunMetricZscoreStore.setState(state => ({
    metricFilter: {...state.metricFilter, ...metricFilter},
  }));
};

const setRunFilter = (runFilter: Partial<RunFilter>) => {
  useRunMetricZscoreStore.setState(state => ({
    runFilter: {...state.runFilter, ...runFilter},
  }));
};

// Validation functions
export const isValidMetricName = (metricName: string | undefined) => {
  return metricName !== undefined && metricName.trim() !== '';
};

export const getNumberValueForField = (field: string | undefined) => {
  const fieldNumber = Number(field || '');
  return fieldNumber;
};

export const isValidThreshold = (threshold: string | undefined) => {
  if (threshold === undefined || threshold === '') {
    return false;
  }
  const thresholdNumber = getNumberValueForField(threshold);
  return (
    !isNaN(thresholdNumber) && isFinite(thresholdNumber) && thresholdNumber > 0
  );
};

export const isValidWindowSize = (windowSize: string | undefined) => {
  if (windowSize === undefined || windowSize === '') {
    return false;
  }
  const windowSizeNumber = getNumberValueForField(windowSize);
  return (
    Number.isInteger(windowSizeNumber) &&
    windowSizeNumber >= 2 &&
    windowSizeNumber <= 100
  );
};

export const isValidChangeDirection = (changeDirection: {
  isAboveChecked: boolean;
  isBelowChecked: boolean;
}) => {
  return changeDirection.isAboveChecked || changeDirection.isBelowChecked;
};

export const getChangeDirectionFromCheckboxes = (
  isAboveChecked: boolean,
  isBelowChecked: boolean
): ChangeDirection => {
  if (isAboveChecked && isBelowChecked) {
    return 'ANY';
  } else if (isAboveChecked) {
    return 'INCREASE';
  } else {
    return 'DECREASE';
  }
};

export const getCheckboxesFromChangeDirection = (
  changeDir: ChangeDirection
): {isAboveChecked: boolean; isBelowChecked: boolean} => {
  return {
    isAboveChecked: changeDir === 'INCREASE' || changeDir === 'ANY',
    isBelowChecked: changeDir === 'DECREASE' || changeDir === 'ANY',
  };
};

export const isValidRunMetricZscoreConfig = (config: RunMetricZscoreConfig) => {
  return (
    isValidMetricName(config.metricFilter.metricName) &&
    isValidThreshold(config.metricFilter.threshold) &&
    isValidWindowSize(config.metricFilter.windowSize) &&
    isValidChangeDirection(config.metricFilter.changeDirection) &&
    isValidRunFilterConfig(config.runFilter)
  );
};

// TODO: make sure this matches the description in google sheets (matt)
export const getRunMetricZscoreString = (
  metricFilter: RunMetricZscoreConfig['metricFilter'],
  runFilter: RunMetricZscoreConfig['runFilter']
) => {
  const metricNameStr = metricFilter.metricName
    ? `${metricFilter.metricName}`
    : '{Metric}';

  const thresholdStr = metricFilter.threshold
    ? `${metricFilter.threshold}`
    : '{Threshold}';

  const changeDir = getChangeDirectionFromCheckboxes(
    metricFilter.changeDirection.isAboveChecked,
    metricFilter.changeDirection.isBelowChecked
  );

  let directionStr = '';
  switch (changeDir) {
    case 'ANY':
      directionStr = `is above ${thresholdStr} or below -${thresholdStr}`;
      break;
    case 'INCREASE':
      directionStr = `is above ${thresholdStr}`;
      break;
    case 'DECREASE':
      directionStr = `is below -${thresholdStr}`;
      break;
  }

  const usernameSnippet =
    runFilter.isUsernameFilterEnabled && runFilter.username
      ? `belonging to ${runFilter.username}, `
      : '';
  const runNameSnippet =
    runFilter.isRunNameFilterEnabled && runFilter.runName
      ? `matching "${runFilter.runName}", `
      : '';

  return `for a run ${usernameSnippet}${runNameSnippet}the z-score for ${metricNameStr} ${directionStr}.`;
};

export const getEventConfigForRunMetricZscore = (
  config: RunMetricZscoreConfig
) => {
  const changeDir = getChangeDirectionFromCheckboxes(
    config.metricFilter.changeDirection.isAboveChecked,
    config.metricFilter.changeDirection.isBelowChecked
  );

  return {
    eventTriggeringConditionType: EventTriggeringConditionType.RunMetricZscore,
    filters: {
      filters: [
        {
          zscoreFilter: {
            metricName: config.metricFilter.metricName,
            threshold: config.metricFilter.threshold,
            windowSize: config.metricFilter.windowSize,
            changeDir,
          },
          runFilter: config.runFilter,
        },
      ],
    },
  };
};

export const RunMetricZscoreSection = () => {
  const metricFilter = useRunMetricZscoreStore(
    useShallow(state => state.metricFilter)
  );
  const runFilter = useRunMetricZscoreStore(
    useShallow(state => state.runFilter)
  );
  console.log({
    log: 'RunMetricZscoreSection',
    metricFilter,
    runFilter,
  });
  const allowAutoFocus = useRunMetricZscoreStore(state => state.allowAutoFocus);

  const showMetricNameError =
    metricFilter.metricName !== undefined &&
    !isValidMetricName(metricFilter.metricName);
  const showThresholdError =
    metricFilter.threshold !== undefined &&
    !isValidThreshold(metricFilter.threshold);
  const showWindowSizeError =
    metricFilter.windowSize !== undefined &&
    !isValidWindowSize(metricFilter.windowSize);
  const showChangeDirectionError = !isValidChangeDirection(
    metricFilter.changeDirection
  );

  const getDirectionText = () => {
    const changeDir = getChangeDirectionFromCheckboxes(
      metricFilter.changeDirection.isAboveChecked,
      metricFilter.changeDirection.isBelowChecked
    );

    switch (changeDir) {
      case 'ANY':
        return `is above or below ${metricFilter.threshold}`;
      case 'INCREASE':
        return `is above ${metricFilter.threshold}`;
      case 'DECREASE':
        return `is below -${metricFilter.threshold}`;
    }
  };

  return (
    <Tailwind>
      <div className="mt-16 font-semibold">Metric setup</div>
      <div className="mt-8 flex flex-col gap-4 rounded-lg border border-moon-200 p-16">
        <S.AutomationDescriptionSection>
          <S.AutomationDescriptionIcon>
            <Icon name="recent-clock" />
          </S.AutomationDescriptionIcon>
          <S.AutomationDescriptionBody>
            <S.AutomationDescriptionTitle>
              In the event that
            </S.AutomationDescriptionTitle>
            <S.AutomationDescriptionText>
              Z-score of {metricFilter.metricName || 'metric'}, compared to the
              average of {metricFilter.metricName || 'metric'} across the
              preceding {metricFilter.windowSize} logged values,{' '}
              {getDirectionText()}.
            </S.AutomationDescriptionText>
          </S.AutomationDescriptionBody>
        </S.AutomationDescriptionSection>
        <SeparatedChunk>
          <BoldLabel>Metric</BoldLabel>
          <div className="mt-4">
            <TextField
              value={metricFilter.metricName ?? ''}
              onChange={value => RunMetricZscoreActions.setMetricName(value)}
              placeholder="Ex. loss"
              autoFocus={
                /* We don't want to auto focus the metric name field when we're in the workspace drawer -
            it causes jankiness when the drawer opens (the text field is focused and it forces the drawer open
            before the animation is complete). We are using "is the metric name field populated" as a proxy here,
            since it makes sense not to auto focus a field that's already been filled in anyway. */
                allowAutoFocus
              }
              errorState={showMetricNameError}
            />
            {showMetricNameError && (
              <ErrorText>Metric name cannot be empty</ErrorText>
            )}
          </div>
        </SeparatedChunk>
        <SeparatedChunk>
          <BoldLabel>
            <div className="flex items-center gap-4">
              Baseline window
              <Tooltip
                content="Defines how many recent logged values are used to calculate the average and standard deviation for detecting outliers."
                trigger={<Icon className="text-moon-400" name="info" />}
              />
            </div>
          </BoldLabel>
          <div className="mt-4 flex items-center gap-8">
            <span>Average across</span>
            <div className="mx-2 inline-block w-48">
              <TextField
                type="number"
                value={metricFilter.windowSize.toString()}
                onChange={value => RunMetricZscoreActions.setWindowSize(value)}
                errorState={showWindowSizeError}
              />
            </div>
            <span>logged values.</span>
          </div>
          {showWindowSizeError && (
            <ErrorText>Integer 2 - 100 required</ErrorText>
          )}
        </SeparatedChunk>
        <SeparatedChunk>
          <BoldLabel>
            <div className="flex items-center gap-4">
              Z-score threshold
              <Tooltip
                content="Z-score is a calculation of how many standard deviations away the current value is from baseline window average."
                trigger={<Icon className="text-moon-400" name="info" />}
              />
            </div>
          </BoldLabel>
          <div className="mt-4">
            <div className="inline-block w-48">
              <TextField
                type="number"
                value={metricFilter.threshold.toString()}
                onChange={value => RunMetricZscoreActions.setThreshold(value)}
                errorState={showThresholdError}
              />
            </div>
          </div>
          {showThresholdError && (
            <ErrorText>Threshold must be a positive number</ErrorText>
          )}
        </SeparatedChunk>
        <SeparatedChunk>
          <BoldLabel>
            <div className="flex items-center">Trigger if z-score is:</div>
          </BoldLabel>
          <div className="mt-4 flex flex-col gap-8">
            <div className="flex items-center gap-8">
              <Checkbox
                checked={metricFilter.changeDirection.isAboveChecked}
                onCheckedChange={checked =>
                  RunMetricZscoreActions.setIsAboveChecked(!!checked)
                }
              />
              <span>Above {metricFilter.threshold}</span>
            </div>
            <div className="flex items-center gap-8">
              <Checkbox
                checked={metricFilter.changeDirection.isBelowChecked}
                onCheckedChange={checked =>
                  RunMetricZscoreActions.setIsBelowChecked(!!checked)
                }
              />
              <span>Below -{metricFilter.threshold}</span>
            </div>
          </div>
          {showChangeDirectionError && (
            <ErrorText>At least one direction must be selected</ErrorText>
          )}
        </SeparatedChunk>
      </div>
      <SeparatedChunk>
        <RunFilterChunk
          runFilter={runFilter}
          actions={{
            ...RunMetricZscoreActions,
          }}
        />
      </SeparatedChunk>
    </Tailwind>
  );
};
